import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ExternalLink, Mail, Pencil, Phone } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { clients as clientsApi } from '@/api/endpoints'
import { EmptyState, Modal, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import ClientCredit from '@/features/clients/ClientCredit'
import ClientForm from '@/features/clients/ClientForm'
import EmailList from '@/features/emails/EmailList'
import { useCodebooks } from '@/hooks/useBootstrap'
import { formatDate, formatDateTime, formatMoney } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { Client, ClientReservation, Paginated } from '@/types/api'
import { paymentStatusLabel } from '@/lib/paymentStatus'

const DEFAULT_PAGE_SIZE = 10

/**
 * Rýchly náhľad klienta. Rezervácie sa strankujú na serveri - stály zákazník
 * ich má za sezónu desiatky a načítať ich všetky kvôli náhľadu nemá zmysel.
 *
 * Samostatná stránka `/clients/:id` zostáva; odkaz na ňu je v hlavičke, aby sa
 * dala poslať alebo otvoriť na novej karte.
 */
export default function ClientDetailModal({
  clientId,
  onClose,
}: {
  clientId: number
  onClose: () => void
}) {
  const { t } = useTranslation()
  const books = useCodebooks()
  const readOnly = !useAuth((s) => s.user)?.can_write
  const isAdmin = Boolean(useAuth((s) => s.user)?.can_administrate)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState(false)

  const { data: client } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => clientsApi.get(clientId),
  })

  const { data: reservations, isLoading: loadingReservations } = useQuery({
    queryKey: ['client-reservations', clientId, page, pageSize],
    queryFn: () => clientsApi.reservations(clientId, page, pageSize),
    placeholderData: keepPreviousData,
  })

  // Endpoint vracia stránku, ale staršie odpovede môžu byť holé pole.
  const paged = reservations as Paginated<ClientReservation> | undefined
  const rows: ClientReservation[] = Array.isArray(reservations) ? reservations : (paged?.results ?? [])
  const count = Array.isArray(reservations) ? reservations.length : (paged?.count ?? 0)
  const pages = Math.max(1, Math.ceil(count / pageSize))

  // Zo skutočných platieb objednávok - nie z príznaku položky, ktorý hovorí
  // len o potvrdení o platbe (platba kartou bez dokladu by bola „nezaplatená“).
  const { data: money } = useQuery({
    queryKey: ['client-money', clientId],
    queryFn: () => clientsApi.money(clientId),
  })
  const spent = Number.parseFloat(money?.charged ?? '0')
  const paid = Number.parseFloat(money?.paid ?? '0')
  const unpaid = Number.parseFloat(money?.due ?? '0')

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={client?.full_name || t('clients.title')}
    >
      {!client ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <ClientFlags client={client} />
            <div className="ml-auto flex items-center gap-3">
              {!readOnly && !editing ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setEditing(true)}
                >
                  <Pencil size={14} />
                  {t('global.edit')}
                </button>
              ) : null}
              <Link
                to={`/clients/${client.id}`}
                className="inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
              >
                {t('clients.openPage')}
                <ExternalLink size={12} />
              </Link>
            </div>
          </div>

          {editing ? (
            <ClientForm client={client} onDone={() => setEditing(false)} />
          ) : (
            <>
              <dl className="grid gap-x-4 gap-y-2 rounded-md border border-slate-200 p-3 text-sm sm:grid-cols-3">
                <Detail label={t('reservation.phone')} value={client.tel} icon={<Phone size={12} />} />
                <Detail label={t('reservation.email')} value={client.email} icon={<Mail size={12} />} />
                <Detail label={t('clients.level')} value={books.label('levels', client.levelid)} />
                <Detail
                  label={t('reservation.language')}
                  value={books.label('languages', client.lang_id)}
                />
                <Detail label={t('reservation.course')} value={books.label('courses', client.course_id)} />
                <Detail label={t('clients.created')} value={formatDate(client.created)} />
                <Detail label={t('clients.totalSpent')} value={formatMoney(spent)} />
                <Detail label={t('clients.paid')} value={formatMoney(paid)} />
                <Detail
                  label={t('clients.unpaid')}
                  value={formatMoney(unpaid)}
                  highlight={unpaid > 0}
                />
                <Detail label={t('clients.reservationsCount')} value={String(count)} />
              </dl>

              <ClientCredit clientId={client.id} />

              {client.note ? (
                <div className="rounded-md bg-slate-50 p-3 text-sm">
                  <div className="mb-1 text-xs font-medium uppercase text-slate-500">
                    {t('global.note')}
                  </div>
                  <p className="whitespace-pre-line">{client.note}</p>
                </div>
              ) : null}
            </>
          )}

          <div className="overflow-x-auto rounded-md border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
              <h3 className="text-sm font-semibold">{t('clients.reservations')}</h3>
              {loadingReservations ? <Spinner /> : null}
            </div>

            {rows.length === 0 ? (
              <EmptyState>{t('app.empty')}</EmptyState>
            ) : (
              <>
                <table className="table-base">
                  <thead>
                    <tr>
                      <th>{t('reservation.date')}</th>
                      <th>{t('reservation.instructor')}</th>
                      <th>{t('reservation.course')}</th>
                      <th>{t('reservation.language')}</th>
                      <th className="text-right">{t('reservation.persons')}</th>
                      <th className="text-right">{t('reservation.price')}</th>
                      <th>{t('reservation.status')}</th>
                      <th>{t('reservation.paid')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td className="whitespace-nowrap">{formatDateTime(row.start)}</td>
                        <td>{row.instructor_name || '—'}</td>
                        <td>{books.label('courses', row.course_id) || '—'}</td>
                        <td>{books.label('languages', row.lang_id) || '—'}</td>
                        <td className="text-right tabular-nums">{row.numbers}</td>
                        <td className="text-right tabular-nums">{formatMoney(row.fees_total)}</td>
                        <td>{books.label('statuses', row.status_id) || '—'}</td>
                        <td>{paymentStatusLabel(t, row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Pagination
                  page={page}
                  pages={pages}
                  pageSize={pageSize}
                  count={count}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size)
                    setPage(1)
                  }}
                />
              </>
            )}
          </div>

          {/* „Dostal to ten klient?“ je najčastejšia otázka na recepcii
              a odpoveď patrí sem, nie do samostatného prehľadu s filtrom.
              Fronta nesie telá správ, preto len pre administrátora. */}
          {isAdmin ? (
            <div className="overflow-hidden rounded-md border border-slate-200">
              <h3 className="border-b border-slate-200 px-3 py-2 text-sm font-semibold">
                {t('emails.title')}
              </h3>
              <EmailList filter={{ client_id: clientId }} pageSize={10} compact />
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  )
}

export function ClientFlags({ client }: { client: Client }) {
  const { t } = useTranslation()
  const flags = [
    client.is_children ? t('global.child') : null,
    client.is_beginner ? t('global.beginner') : null,
  ].filter(Boolean)

  if (flags.length === 0) return null
  return (
    <>
      {flags.map((flag) => (
        <span key={flag} className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
          {flag}
        </span>
      ))}
    </>
  )
}

function Detail({
  label,
  value,
  icon,
  highlight,
}: {
  label: string
  value: string | null | undefined
  icon?: React.ReactNode
  highlight?: boolean
}) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-xs uppercase tracking-wide text-slate-500">
        {icon}
        {label}
      </dt>
      <dd className={highlight ? 'font-medium text-red-600' : 'font-medium'}>{value || '—'}</dd>
    </div>
  )
}
